import {type FishGroup, type WriteCVAttribute, type WriteNameAttribute, type WriteQualityModelAttribute} from 'client';
import {type IFishGroup} from './reducer';
import backendCommunicator from 'services/backendCommunicator';
import {FormSelectionEnum, type IFormData} from 'interfaces';

export const editFishGroups = async (writeFishGroups: IFishGroup) => {
  const {fishGroupIds, planId} = writeFishGroups;
  const cvAttribute = handleCV(writeFishGroups);
  return await backendCommunicator.updateFishGroups({
    planId,
    fishGroupIds,
    nameAttribute: handleAttribute(writeFishGroups.nameAttribute, writeFishGroups.nameEdited),
    qualityModelAttribute: handleAttribute(writeFishGroups.qualityModelAttribute, writeFishGroups.qualityModelEdited),
    propertiesAttribute: handlePropertiesAttribute(writeFishGroups.fishGroups[0], writeFishGroups.propertiesEdited),
    cvAttribute,
  });
};

export const handleCV = (writeFishGroups: IFishGroup): WriteCVAttribute | undefined => {
  if (writeFishGroups.cvEdited) {
    if (writeFishGroups.cvAttribute) {
      if (!writeFishGroups.cvAttribute?.isPinned) {
        return {isPinned: false};
      }
      const value =
        writeFishGroups.cvAttribute.value === null || writeFishGroups.cvAttribute.value === undefined
          ? undefined
          : writeFishGroups.cvAttribute.value / 100;
      return {...writeFishGroups.cvAttribute, value};
    }
  }
  return undefined;
};

const handleAttribute = <T extends {isPinned: boolean} | undefined>(attribute: T, edited: boolean) => {
  if (edited) {
    if (attribute?.isPinned === false) {
      return {isPinned: false};
    }
    return attribute;
  }
  return undefined;
};

const handlePropertiesAttribute = (fishGroup: FishGroup, edited: boolean) => {
  if (!edited) {
    return undefined;
  }

  const {isPinned, value} = fishGroup.propertiesAttribute;

  const valueToSend = Array.isArray(value) ? value.map((v) => v.id) : value;

  return {
    isPinned,
    value: isPinned ? valueToSend : undefined,
  };
};

const outActions = [
  FormSelectionEnum.harvest,
  FormSelectionEnum.transferOut,
  FormSelectionEnum.delousing,
  FormSelectionEnum.liveFishTransaction,
];

export const resolveFishGroupIds = (formData: IFormData): string[] | undefined => {
  if (formData.fishGroupIds) {
    return formData.fishGroupIds;
  }
  if (formData.actionType && outActions.includes(formData.actionType) && formData.fromFishGroupId) {
    return [formData.fromFishGroupId];
  }
  if (formData.toFishGroupId) {
    return [formData.toFishGroupId];
  }
  return undefined;
};

export const handleInitialFishGroupCV = (fishGroups: FishGroup[], fishGroup: FishGroup): WriteCVAttribute => {
  const isPinned = fishGroups.some((fishGroup) => fishGroup.cvAttribute.isPinned);
  const value = fishGroup.cvAttribute.value ? fishGroup.cvAttribute.value * 100 : undefined;
  return {
    value,
    isPinned,
  };
};

export const handleInitialFishGroupName = (fishGroups: FishGroup[]): WriteNameAttribute => {
  const namesAreDifferent = fishGroups.some(
    (fishGroup) => fishGroup.nameAttribute.value !== fishGroups[0].nameAttribute.value,
  );
  let value = fishGroups[0].nameAttribute.value;
  if (namesAreDifferent) {
    value = 'mixed';
  }
  const isPinned = fishGroups.some((fishGroup) => fishGroup.nameAttribute.isPinned);
  return {
    value,
    isPinned,
  };
};

export const handleInitialFishGroupQuality = (
  fishGroups: FishGroup[],
  fishGroup: FishGroup,
): WriteQualityModelAttribute => {
  const isPinned = fishGroups.some((fishGroup) => fishGroup.qualityModelAttribute.isPinned);
  return {
    ...fishGroup.qualityModelAttribute,
    isPinned,
  };
};

export const filterFishGroupsFromHook = (fishGroups: FishGroup[], fishGroupIds: string[]): FishGroup[] => {
  if (fishGroups.length >= fishGroupIds.length) {
    return fishGroups.filter((fishGroup) => fishGroupIds.includes(fishGroup.id));
  }
  return fishGroups;
};
